import React from "react";
import Button from "../../Button";

import style from "./styles.module.scss";

import "@material/web/textfield/filled-text-field.js";
import "@material/web/textfield/outlined-text-field.js";

export type InputVariant = "filled" | "outlined";

export type InputType =
  | "datetime-local"
  | "email"
  | "number"
  | "password"
  | "search"
  | "tel"
  | "text"
  | "textarea"
  | "url";

export interface MaterialTextFieldElement extends HTMLElement {
  value: string;
  valueAsNumber: number;
  disabled: boolean;
  error: boolean;
  validity: ValidityState;
  validationMessage: string;
  checkValidity(): boolean;
  reportValidity(): boolean;
  select(): void;
  setCustomValidity(message: string): void;
}

export type MaterialInputEvent = React.ChangeEvent<
  MaterialTextFieldElement
>;

export type MaterialInputEventHandler = (
  event: MaterialInputEvent,
) => void;

export interface InputMaskValue {
  mask(): string;
  unmask(): string;
}

export type InputMask = (value: string) => InputMaskValue;

interface InputIconProps extends React.HTMLAttributes<HTMLElement> {
  "aria-label"?: string;
}

interface MaterialProperties {
  [key: string]: unknown;
}

export type InputProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  "defaultValue" | "onChange" | "onInput"
> & {
  variant?: InputVariant;
  type?: InputType;
  label?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  supportingText?: string;
  error?: boolean;
  errorText?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  noAsterisk?: boolean;
  multiple?: boolean;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  prefixText?: string;
  suffixText?: string;
  min?: string | number;
  max?: string | number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  step?: string | number;
  rows?: number;
  cols?: number;
  noSpinner?: boolean;
  mask?: InputMask;
  leadingIcon?: React.ReactElement<InputIconProps>;
  trailingIcon?: React.ReactElement<InputIconProps>;
  clearButtonAriaLabel?: string;
  hideClearButton?: boolean;
  fullWidth?: boolean;
  onInput?: MaterialInputEventHandler;
  onChange?: MaterialInputEventHandler;
};

const variantElements: { [key in InputVariant]: string } = {
  filled: "md-filled-text-field",
  outlined: "md-outlined-text-field",
};

function addBooleanAttribute(
  properties: MaterialProperties,
  name: string,
  enabled: boolean,
) {
  if (enabled) {
    properties[name] = "";
  }
}

function addDefinedProperty(
  properties: MaterialProperties,
  name: string,
  value: unknown,
) {
  if (value !== undefined) {
    properties[name] = value;
  }
}

function applyInputMask(value: string, mask?: InputMask): string {
  return mask ? mask(value).mask() : value;
}

const Input = React.forwardRef<MaterialTextFieldElement, InputProps>(
  (
    {
      variant = "filled",
      type = "text",
      label,
      name,
      value,
      defaultValue,
      placeholder,
      supportingText,
      error = false,
      errorText,
      autoFocus = false,
      disabled = false,
      required = false,
      readOnly = false,
      noAsterisk = false,
      multiple = false,
      autoComplete,
      inputMode,
      prefixText,
      suffixText,
      min,
      max,
      minLength,
      maxLength,
      pattern,
      step,
      rows,
      cols,
      noSpinner = false,
      mask,
      leadingIcon,
      trailingIcon,
      clearButtonAriaLabel = "Limpar campo",
      hideClearButton = false,
      fullWidth = false,
      onInput,
      onChange,
      className = "",
      ...htmlAttributes
    },
    ref,
  ) => {
    const initialValue = value !== undefined ? value : defaultValue;
    const inputRef = React.useRef<MaterialTextFieldElement | null>(null);
    const [hasValue, setHasValue] = React.useState(
      Boolean(initialValue && applyInputMask(initialValue, mask)),
    );

    React.useEffect(() => {
      if (value !== undefined) {
        setHasValue(Boolean(applyInputMask(value, mask)));
      }
    }, [mask, value]);

    const setInputRef = (element: MaterialTextFieldElement | null) => {
      inputRef.current = element;

      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        (ref as React.MutableRefObject<MaterialTextFieldElement | null>).current = element;
      }
    };

    React.useEffect(() => {
      if (!rows) {
        return;
      }

      const element = inputRef.current;
      const shadow = element?.shadowRoot;

      if (!shadow) {
        return;
      }

      const styleElement = document.createElement("style");
      styleElement.textContent = ".icon.trailing { align-self: flex-start; }";
      shadow.appendChild(styleElement);

      return () => {
        styleElement.remove();
      };
    }, [rows]);

    const classes = [
      style.input,
      fullWidth ? style.inputFullWidth : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const handleInput: MaterialInputEventHandler = (event) => {
      event.currentTarget.value = applyInputMask(
        event.currentTarget.value,
        mask,
      );
      setHasValue(Boolean(event.currentTarget.value));

      if (onInput) {
        onInput(event);
      }
    };

    const handleChange: MaterialInputEventHandler = (event) => {
      event.currentTarget.value = applyInputMask(
        event.currentTarget.value,
        mask,
      );
      setHasValue(Boolean(event.currentTarget.value));

      if (onChange) {
        onChange(event);
      }
    };

    const handleClear = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const input = inputRef.current;

      if (!input) {
        return;
      }

      input.value = "";
      setHasValue(false);
      input.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
      input.focus();
    };

    const showClearButton = hasValue && !disabled && !readOnly && !hideClearButton;
    const hasTrailingIcon = showClearButton || Boolean(trailingIcon);

    const materialProperties: MaterialProperties = {
      ...htmlAttributes,
      ref: setInputRef,
      class: classes,
      onChange: handleChange,
      onInput: handleInput,
      type,
    };

    addDefinedProperty(materialProperties, "label", label);
    addDefinedProperty(materialProperties, "name", name);
    addDefinedProperty(
      materialProperties,
      "value",
      initialValue !== undefined
        ? applyInputMask(initialValue, mask)
        : undefined,
    );
    addDefinedProperty(materialProperties, "placeholder", placeholder);
    addDefinedProperty(
      materialProperties,
      "supporting-text",
      supportingText,
    );
    addDefinedProperty(materialProperties, "error-text", errorText);
    addDefinedProperty(materialProperties, "autocomplete", autoComplete);
    addDefinedProperty(materialProperties, "inputmode", inputMode);
    addDefinedProperty(materialProperties, "prefix-text", prefixText);
    addDefinedProperty(materialProperties, "suffix-text", suffixText);
    addDefinedProperty(materialProperties, "min", min);
    addDefinedProperty(materialProperties, "max", max);
    addDefinedProperty(materialProperties, "minlength", minLength);
    addDefinedProperty(materialProperties, "maxlength", maxLength);
    addDefinedProperty(materialProperties, "pattern", pattern);
    addDefinedProperty(materialProperties, "step", step);
    addDefinedProperty(materialProperties, "rows", rows);
    addDefinedProperty(materialProperties, "cols", cols);

    addBooleanAttribute(materialProperties, "error", error);
    addBooleanAttribute(materialProperties, "autofocus", autoFocus);
    addBooleanAttribute(materialProperties, "disabled", disabled);
    addBooleanAttribute(materialProperties, "required", required);
    addBooleanAttribute(materialProperties, "readonly", readOnly);
    addBooleanAttribute(materialProperties, "no-asterisk", noAsterisk);
    addBooleanAttribute(materialProperties, "multiple", multiple);
    addBooleanAttribute(materialProperties, "no-spinner", noSpinner);
    addBooleanAttribute(
      materialProperties,
      "has-leading-icon",
      Boolean(leadingIcon),
    );
    addBooleanAttribute(
      materialProperties,
      "has-trailing-icon",
      hasTrailingIcon,
    );

    const slottedLeadingIcon = leadingIcon
      ? React.cloneElement(leadingIcon, {
          "key": "leading-icon",
          "slot": "leading-icon",
          "className": [style.inputIcon, leadingIcon.props.className]
            .filter(Boolean)
            .join(" "),
          "aria-hidden": leadingIcon.props["aria-label"] ? undefined : true,
        })
      : null;

    const slottedTrailingIcon = showClearButton
      ? (
        <Button
          key="clear-button"
          slot="trailing-icon"
          variant="text"
          size="extra-small"
          type="button"
          iconButton
          className={style.clearButton}
          aria-label={clearButtonAriaLabel}
          onClick={handleClear}
          icon={(
            <span
              className={`material-symbols-sharp ${style.clearIcon}`}
              aria-hidden="true"
            >
              cancel
            </span>
          )}
        />
      )
      : trailingIcon
      ? React.cloneElement(trailingIcon, {
          "key": "trailing-icon",
          "slot": "trailing-icon",
          "className": [style.inputIcon, trailingIcon.props.className]
            .filter(Boolean)
            .join(" "),
          "aria-hidden": trailingIcon.props["aria-label"] ? undefined : true,
        })
      : null;

    return React.createElement(
      variantElements[variant],
      materialProperties,
      slottedLeadingIcon,
      slottedTrailingIcon,
    );
  },
);

Input.displayName = "Input";

export default Input;
